import { useEffect } from 'react'
import type {
  Country,
  Currency,
  PlanInfo,
  PricingDatum,
  BillingCycle
} from '@/types/apply'
import { buildReadOnlyWebMcpTools, registerWebMcpTools, hasModelContext } from '@/hooks/webMcpTools'

// Exposes the two READ-ONLY WebMCP tools (list plans/pricing, check country
// eligibility) to AI agents visiting the homepage, so an agent that lands on
// wavbee.com can discover what the site costs and whether a country can apply
// without first navigating to /apply.
//
// This is deliberately the read-only pair only: the homepage has no application
// form, so there is nothing to prefill. Like `useWebMcp`, it is fully
// feature-detected (a no-op when `navigator.modelContext` is absent) and
// withdraws its tools on unmount.
//
// THE DATA IS FETCHED, NOT SHIPPED — and only once we know something will use
// it. The plan/pricing/country tables are ~27 KB of JSON that render NOTHING:
// they exist purely to answer an agent's tool call. Passing them as an Inertia
// prop put all 27 KB into the HTML of every homepage view, where they were 64%
// of the entire `data-page` payload and had to be parsed before hydration could
// finish — on a page whose Core Web Vitals problem was render delay.
//
// WebMCP needs `navigator.modelContext`, which needs JavaScript, so nothing can
// consume this data without running the check below. That makes the fetch free
// of cost for real visitors and lossless for agents: a client that cannot see
// the tools could not have called them anyway.

export interface UseHomeWebMcpArgs {
  plans: Record<string, PlanInfo[]>
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  countries: Country[]
  supportedCurrencies: Currency[]
  defaultCurrency: Currency
}

/** The server's shape, which is snake_case. Mapped once, here. */
interface HomeWebMcpPayload {
  plans: Record<string, PlanInfo[]>
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  countries: Country[]
  supported_currencies: Currency[]
  default_currency: Currency
}

/**
 * @param source Either the URL to fetch the tool data from (the homepage's
 *   normal path), or the data itself — which the apply page already has in
 *   memory for its form and should not re-fetch.
 */
export function useHomeWebMcp(source: string | UseHomeWebMcpArgs) {
  useEffect(() => {
    // FEATURE-DETECT BEFORE FETCHING. This is the whole point: in a browser
    // without WebMCP — every mainstream one today — this hook makes no request
    // and does no work.
    if (!hasModelContext()) return

    if (typeof source !== 'string') return registerWebMcpTools(buildReadOnlyWebMcpTools(source))

    const abort = new AbortController()
    let withdraw: (() => void) | undefined

    fetch(source, { signal: abort.signal, headers: { Accept: 'application/json' } })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: HomeWebMcpPayload) => {
        if (abort.signal.aborted) return
        withdraw = registerWebMcpTools(buildReadOnlyWebMcpTools({
          plans: data.plans,
          pricing: data.pricing,
          countries: data.countries,
          supportedCurrencies: data.supported_currencies,
          defaultCurrency: data.default_currency
        }))
      })
      .catch(() => {
        // An agent that cannot load the tool data simply gets no tools. There is
        // no human-visible feature here to degrade, so there is nothing to say.
      })

    return () => {
      abort.abort()
      withdraw?.()
    }
  }, [source])
}
